import { Box, Typography } from '@mui/material'
import { filesize } from 'filesize'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect } from 'react'
import { getIconForExtension, isArray } from '../../../utils/helpers'
import DownloadIcon from '../../../images/Download-Icon.svg'
import {
  attachmentGetGroupAttachmentsByGroupIds, attachmentResetAlertHandler, downloadAttachmentById,
} from '../../../redux/features/attachment/attachmentThunk'
import { selectAttachment } from '../../../redux/features/attachment/attachmentSlice'
import AttachmentDownloaderSkeleton from './AttachmentDownloaderSkeleton'
import FormModalContainer from '../FormModalContainer'
import UseTranslation from '../../../hooks/UseTranslation'
import LoaderWithErrorHandler from '../../PageHelpers/LoaderWithErrorHandler'
import OfoqThemeBtn from '../../Buttons/OfoqThemeBtn'

const AttachmentDownloaderModal = ({
  formModalState,
  setFormModalState,
  attachments,
  customStyle,
}) => {
  const { t } = UseTranslation()
  const dispatch = useDispatch()

  const AttachmentStore = useSelector(selectAttachment)

  useEffect(() => {
    if (formModalState) {
      dispatch(attachmentGetGroupAttachmentsByGroupIds({
        queries: {
          attachment_group_ids: isArray(attachments) ? [...new Set(attachments.map((attachment) => attachment.groupUUID))].join(',') : attachments,
        },
      }))
    }
  }, [formModalState])

  const handleDownload = (groupId, attachmentId, name) => {
    dispatch(downloadAttachmentById({
      queries: {
        groupId,
        attachmentId,
        name,
      },
    })).then((response) => {
      if (response.payload?.url) {
        const isPdf = response.payload.name?.toLowerCase().endsWith('.pdf');
        if (isPdf) {
          window.open(response.payload.url, '_blank');
        } else {
          const link = document.createElement('a');
          link.href = response.payload.url;
          link.download = response.payload.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    })
  }

  const cancelHandler = () => {
    setFormModalState(false)
  }

  const getTotalAttachmentCount = () => {
    if (isArray(attachments)) {
      return attachments?.length
    }
    if (isArray(AttachmentStore?.lookups)) {
      // eslint-disable-next-line max-len
      return AttachmentStore?.lookups?.reduce((total, group) => total + (group.attachments?.length || 0), 0)
    }
    return AttachmentStore?.lookups?.attachments?.length || 0
  }

  const getFlattenedAttachments = () => {
    if (isArray(AttachmentStore?.lookups)) {
      return AttachmentStore?.lookups?.flatMap((group) => group.attachments?.map((file) => ({
        ...file,
        groupId: group.id,
        uploaded_by: group.uploaded_by,
      })) || [])
    }
    if (AttachmentStore?.lookups?.attachments) {
      return AttachmentStore?.lookups?.attachments?.map((file) => ({
        ...file,
        groupId: AttachmentStore?.lookups?.id,
        uploaded_by: AttachmentStore?.lookups?.uploaded_by,
      }))
    }
    return []
  }

  return (
    <Box>
      <FormModalContainer
        modalTitle={`${getTotalAttachmentCount()} ${t?.Attachments}`}
        modalState={formModalState}
        customWidth={styles.attachmentDownloaderWidth}
        handleClose={cancelHandler}
        cancelText={t?.Close}
      >
        <Box sx={[styles.selectedFilesContainer, customStyle]}>
          {
            getFlattenedAttachments()?.map((file) => (
              <Box
                key={file.id}
                sx={styles.files}
              >
                <Box sx={styles.iconWithText}>
                  {getIconForExtension(file?.name)}
                  <Box sx={styles.fileDetails}>
                    <Typography
                      variant="heading4"
                      sx={styles.fileName}
                    >
                      {file?.name}
                    </Typography>
                    <Typography variant="heading7" sx={styles.fileSize}>
                      {filesize(file?.size)}
                    </Typography>
                    <Box>
                      <Typography variant="heading9" sx={styles.fileSize}>
                        {t?.['Uploaded by']}
                      </Typography>
                      <Typography variant="heading9" sx={styles.employeeName}>
                        {file?.uploaded_by?.name}
                      </Typography>
                    </Box>
                  </Box>

                </Box>
                <Box
                  onClick={() => handleDownload(file?.groupId, file?.id, file?.name)}
                  component="img"
                  src={DownloadIcon}
                  alt="download"
                  sx={styles.DownloadIcon}
                />
              </Box>
            ))
          }
          <LoaderWithErrorHandler
            store={AttachmentStore}
            resetAlertHandler={attachmentResetAlertHandler}
            Skeleton={AttachmentDownloaderSkeleton}
          />
        </Box>
        <Box sx={styles.btnsContainer}>
          <OfoqThemeBtn
            style={styles.cancelBtn}
            name={`${t?.Close}`}
            onClick={cancelHandler}
          />
        </Box>
      </FormModalContainer>
    </Box>
  )
}

const styles = {
  attachmentDownloaderWidth: {
    xs: 'initial',
    sm: '450px',
    md: '602px',
  },
  selectedFilesContainer: {
    marginBlock: '10px',
    overflowY: 'auto',
    maxHeight: '332px',
  },
  files: {
    height: '90px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    borderBottomWidth: { xs: '0', sm: '1px' },
    borderBottomStyle: 'solid',
    borderBottomColor: 'grey.200',
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  iconWithText: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    '& svg': {
      width: {
        xs: '25px', sm: '30px', lg: '35px',
      },
      height: { xs: '25px', sm: '40px', lg: '45px' },
    },
  },
  fileDetails: {
    display: 'flex',
    alignItems: 'start',
    flexDirection: 'column',
  },
  fileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  fileSize: {
    color: 'text.hint',
    marginRight: '5px',
  },
  employeeName: {
    color: 'primary.main',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  DownloadIcon: {
    cursor: 'pointer',
    width: { xs: '25px', sm: '30px', lg: '35px' },
    height: { xs: '25px', sm: '30px', lg: '35px' },
  },
  btnsContainer: {
    display: 'flex',
    marginLeft: 'auto',
    justifyContent: 'flex-end',
    padding: '10px 16px 0 16px',
    margin: '5 -16px',
    borderTopWidth: { xs: '0', sm: '1px' },
    borderTopStyle: 'solid',
    borderTopColor: 'grey.200',
  },
  cancelBtn: {
    display: { xs: 'none', sm: 'block' },
    marginLeft: { xs: 'unset', sm: '10px' },
    width: { xs: '100%', sm: '96px' },
    minWidth: { xs: '80px', sm: '96px' },
    textAlign: 'center',
  },
}

export default AttachmentDownloaderModal
